import { TICKET_STATUS_CLOSED } from '@/utils/const';

/**
 * A support ticket's lifecycle, as the API keeps it (its utils/ticket_lifecycle):
 *
 *   Submitted › Assigned › In progress › Resolved › Closed
 *
 * Staff move a ticket along it in the admin; an organization closes or
 * re-opens it in its app. Each move leaves a line in the conversation, which
 * the other side hears about like a message. The same module lives in the
 * admin and in both organization apps.
 */

export const TICKET_STAGE_SUBMITTED = 'submitted';
export const TICKET_STAGE_ASSIGNED = 'assigned';
export const TICKET_STAGE_IN_PROGRESS = 'in_progress';
export const TICKET_STAGE_RESOLVED = 'resolved';
export const TICKET_STAGE_CLOSED = 'closed';

export interface TicketStageInfo {
  key: string;
  label: string;
  /** The primeicons glyph the stage wears. */
  icon: string;
}

/** The lifecycle, in order. */
export const TICKET_STAGES: ReadonlyArray<TicketStageInfo> = [
  { key: TICKET_STAGE_SUBMITTED, label: 'Submitted', icon: 'pi-send' },
  { key: TICKET_STAGE_ASSIGNED, label: 'Assigned', icon: 'pi-user' },
  { key: TICKET_STAGE_IN_PROGRESS, label: 'In progress', icon: 'pi-sync' },
  { key: TICKET_STAGE_RESOLVED, label: 'Resolved', icon: 'pi-check-circle' },
  { key: TICKET_STAGE_CLOSED, label: 'Closed', icon: 'pi-lock' },
];

/** Still being worked on: what an organization's "Open" filter keeps. */
export const TICKET_OPEN_STAGES: ReadonlyArray<string> = [
  TICKET_STAGE_SUBMITTED,
  TICKET_STAGE_ASSIGNED,
  TICKET_STAGE_IN_PROGRESS,
];

/** Where PUT /ticket/:id/status sends a ticket. */
export const TICKET_MOVE_IN_PROGRESS = 'in_progress';
export const TICKET_MOVE_RESOLVED = 'resolved';
export const TICKET_MOVE_CLOSED = 'closed';
export const TICKET_MOVE_REOPENED = 'reopened';

interface TicketStageLike {
  stage?: string | null;
  status?: string | null;
}

/** Where a ticket stands: as the API says, or — from an API before the lifecycle — what its status says. */
export function ticketStageOf(ticket?: TicketStageLike | null): string {
  const stage = ticket?.stage;
  if (stage && TICKET_STAGES.some((item) => item.key === stage)) return stage;
  return ticket?.status === TICKET_STATUS_CLOSED ? TICKET_STAGE_CLOSED : TICKET_STAGE_SUBMITTED;
}

export function ticketStageIndex(stage: string): number {
  return TICKET_STAGES.findIndex((item) => item.key === stage);
}

export function ticketStageInfo(stage: string): TicketStageInfo {
  return TICKET_STAGES.find((item) => item.key === stage) || TICKET_STAGES[0];
}

export function ticketStageLabel(stage: string): string {
  return ticketStageInfo(stage).label;
}

/* ------------------------------ conversation ----------------------------- */

/** The lines a move leaves in the conversation (the API's TICKET_EVENT_TYPES). */
export const TICKET_EVENT_ASSIGN = 'ticket-assign';
export const TICKET_EVENT_PROGRESS = 'ticket-progress';
export const TICKET_EVENT_RESOLVE = 'ticket-resolve';
export const TICKET_EVENT_CLOSE = 'ticket-close';
export const TICKET_EVENT_REOPEN = 'ticket-reopen';

export interface TicketEventInfo {
  icon: string;
  /** The stage colours the line wears: submitted, assigned, in_progress, resolved, closed, reopened. */
  tone: string;
  /** What "<who> …" did, read by staff — and by the organization. */
  staff: string;
  organization: string;
  /** "Your ticket … is in progress" — the toast an organization sees. */
  notice: string;
}

export const TICKET_EVENTS: Readonly<Record<string, TicketEventInfo>> = {
  [TICKET_EVENT_ASSIGN]: {
    icon: 'pi-user',
    tone: TICKET_STAGE_ASSIGNED,
    staff: 'assigned the ticket',
    organization: 'picked up your ticket',
    notice: 'was picked up',
  },
  [TICKET_EVENT_PROGRESS]: {
    icon: 'pi-sync',
    tone: TICKET_STAGE_IN_PROGRESS,
    staff: 'started working on it',
    organization: 'started working on your ticket',
    notice: 'is in progress',
  },
  [TICKET_EVENT_RESOLVE]: {
    icon: 'pi-check-circle',
    tone: TICKET_STAGE_RESOLVED,
    staff: 'resolved the ticket',
    organization: 'resolved your ticket',
    notice: 'was resolved',
  },
  [TICKET_EVENT_CLOSE]: {
    icon: 'pi-lock',
    tone: TICKET_STAGE_CLOSED,
    staff: 'closed the ticket',
    organization: 'closed the ticket',
    notice: 'was closed',
  },
  [TICKET_EVENT_REOPEN]: {
    icon: 'pi-lock-open',
    tone: 'reopened',
    staff: 're-opened the ticket',
    organization: 're-opened the ticket',
    notice: 'was re-opened',
  },
};

/** How a line in the conversation reads, when it is a move rather than a message. */
export function ticketEventOf(type?: string | null): TicketEventInfo | null {
  return (type && TICKET_EVENTS[type]) || null;
}

/* -------------------------------- notices -------------------------------- */

const NOTICE_SUBJECT_MAX = 60;

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/** A ticket's subject in a toast: the start of it, safe to show as HTML. */
export function noticeSubject(subject?: string | null): string {
  const text = String(subject || '').replace(/\s+/g, ' ').trim();
  const short = text.length > NOTICE_SUBJECT_MAX ? `${text.slice(0, NOTICE_SUBJECT_MAX - 1).trimEnd()}…` : text;
  return escapeHtml(short);
}

/** "Your ticket “Lobby screen is black” is in progress." */
export function ticketMoveNotice(type: string, subject?: string | null): string {
  const event = ticketEventOf(type);
  const what = subject ? `Your ticket “${noticeSubject(subject)}”` : 'Your ticket';
  return `${what} ${event ? event.notice : 'was updated'}.`;
}

/** "Abel Tesfaye assigned you “Lobby screen is black” · Beth Israel." */
export function ticketAssignedNotice(by: string, subject?: string | null, organization?: string | null): string {
  const where = organization ? ` · ${escapeHtml(organization)}` : '';
  return `${escapeHtml(by)} assigned you “${noticeSubject(subject)}”${where}.`;
}
